import { Category, Country, Listing, OfferType } from '@smg-real-estate/hg-rets-schema';

import { isNonSwissBorderRegion } from '@/helpers/ListingData/ListingData';

const LISTERS_WITH_PROPRIETARY_DEPOSIT_OFFERS = ['hpvrem', 'hpv'];
const ALLOWED_MORTGAGE_CALCULATOR_CATEGORIES = new Set([
  Category.Apartment,
  Category.Attic,
  Category.AtticFlat,
  Category.BifamiliarHouse,
  Category.Castle,
  Category.Chalet,
  Category.Duplex,
  Category.FarmHouse,
  Category.FurnishedFlat,
  Category.GrannyFlat,
  Category.House,
  Category.Loft,
  Category.RoofFlat,
  Category.RowHouse,
  Category.RusticHouse,
  Category.SingleHouse,
  Category.SingleRoom,
  Category.Studio,
  Category.TerraceFlat,
  Category.TerraceHouse,
  Category.Villa,
]);
const ALLOWED_RESIDENTIAL_CATEGORIES: Category[] = [Category.Apartment, Category.House];
const ALLOWED_COMMERCIAL_CATEGORIES: Category[] = [
  Category.Office,
  Category.Shop,
  Category.Commercial,
  Category.Workshop,
  Category.Atelier,
  Category.IndustrialObject,
  Category.Practice,
  Category.Factory,
  Category.DepartmentStore,
];
const ALL_SWISSCAUTION_CATEGORIES = ALLOWED_RESIDENTIAL_CATEGORIES.concat(
  ALLOWED_COMMERCIAL_CATEGORIES,
);

export enum SwissCautionListingType {
  RESIDENTIAL = 'residential',
  COMMERCIAL = 'commercial',
}

export const isLocationValidForUbs = (listing: Listing): boolean => {
  return (
    listing?.address?.country === Country.Ch && !isNonSwissBorderRegion(listing.address?.postalCode)
  );
};

export function isListingValidForMortgageCalculations(listing: Listing): boolean {
  return Boolean(
    listing.offerType === OfferType.Buy &&
    listing.prices?.buy?.price &&
    listing.prices?.buy?.price > 0 &&
    listing.prices?.buy?.price <= 50_000_000 &&
    listing.categories?.some((category) => ALLOWED_MORTGAGE_CALCULATOR_CATEGORIES.has(category)) &&
    isLocationValidForUbs(listing),
  );
}

export const isListingValidForMortgage = (listing: Listing): boolean => {
  try {
    validateListingForMortgage(listing);
    return true;
  } catch {
    return false;
  }
};

export const isListingValidForSwissCaution = (listing: Listing): boolean => {
  return (
    listing.offerType === OfferType.Rent &&
    listing.address?.country === Country.Ch &&
    !LISTERS_WITH_PROPRIETARY_DEPOSIT_OFFERS.includes(listing.lister?.id || '') &&
    ALL_SWISSCAUTION_CATEGORIES.some((item) => listing.categories?.includes(item))
  );
};

export const isResidentialListing = (listing: Listing): boolean => {
  return ALLOWED_RESIDENTIAL_CATEGORIES.some((item) => listing.categories?.includes(item));
};

export const isCommercialListing = (listing: Listing): boolean => {
  return ALLOWED_COMMERCIAL_CATEGORIES.some((item) => listing.categories?.includes(item));
};

export const getSwissCautionListingType = (listing: Listing): SwissCautionListingType => {
  return isCommercialListing(listing)
    ? SwissCautionListingType.COMMERCIAL
    : SwissCautionListingType.RESIDENTIAL;
};

const validateListingForMortgage = (listing: Listing): void | never => {
  const address = notEmpty('listing.address', listing.address);
  const categories = notEmpty('listing.categories', listing.categories);
  const country = notEmpty('listing.address.country', address.country);
  const offerType = notEmpty('listing.offerType', listing.offerType);
  notEmpty('listing.address.postalCode', address.postalCode);

  assertTrue(!!/^\d{4}$/.exec(address.postalCode as string), 'Not a valid postalCode');
  assertTrue(offerType === OfferType.Buy, 'Not a buy object');
  assertTrue(country === 'CH', 'Country should be CH');
  assertTrue(categories.length > 0, 'No categories defined');
  assertTrue(
    categories.some((it) => ALLOWED_MORTGAGE_CALCULATOR_CATEGORIES.has(it)),
    'No categories allowed',
  );
};

const assertTrue = (value: boolean, msg: string) => {
  if (!value) {
    throw msg;
  }
};

const notEmpty = <T>(path: string, value: T | null | undefined): T => {
  assertTrue(!!value, `Missing ${path}`);
  return value as T;
};
